import { Fragment, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  getUsers,
  createUser,
  updateUser,
  deleteUser,
  resetUserMfa,
  resendWelcomeEmail,
  getUserEmails,
  addUserEmail,
  updateUserEmail,
  deleteUserEmail,
} from '../api/client'
import type { User } from '../types'
import type { UserEmailOut } from '../api/users'
import { useStore } from '../store'

interface EditState {
  email: string
  initials: string
  is_admin: boolean
  password: string
}

const EMPTY_EDIT: EditState = { email: '', initials: '', is_admin: false, password: '' }

function UserEmailManager({ userId }: { userId: number }) {
  const { t } = useTranslation()
  const [emails, setEmails] = useState<UserEmailOut[]>([])
  const [loading, setLoading] = useState(true)
  const [newEmail, setNewEmail] = useState('')
  const [newLabel, setNewLabel] = useState('')
  const [adding, setAdding] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const load = async () => {
    try {
      setEmails(await getUserEmails(userId))
    } catch (e) {
      setError(e instanceof Error ? e.message : t('user_management.email_manager.error_load'))
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { load() }, [userId])

  const handleAdd = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!newEmail.trim()) return
    setAdding(true)
    setError(null)
    try {
      await addUserEmail(userId, { email: newEmail.trim(), label: newLabel.trim() || null })
      setNewEmail('')
      setNewLabel('')
      await load()
    } catch (e) {
      setError(e instanceof Error ? e.message : t('user_management.email_manager.error_add'))
    } finally {
      setAdding(false)
    }
  }

  const handleSetPrimary = async (id: number) => {
    setError(null)
    try {
      await updateUserEmail(userId, id, { is_primary: true })
      await load()
    } catch (e) {
      setError(e instanceof Error ? e.message : t('user_management.email_manager.error_update'))
    }
  }

  const handleDelete = async (id: number) => {
    setError(null)
    try {
      await deleteUserEmail(userId, id)
      await load()
    } catch (e) {
      setError(e instanceof Error ? e.message : t('user_management.email_manager.error_delete'))
    }
  }

  if (loading) return <p className="text-xs text-gray-400">{t('common.loading')}</p>

  return (
    <div className="space-y-3">
      {error && <p className="text-xs text-red-600">{error}</p>}

      {emails.length === 0 ? (
        <p className="text-xs text-gray-400 dark:text-gray-500">{t('user_management.email_manager.empty')}</p>
      ) : (
        <ul className="space-y-1.5">
          {emails.map(e => (
            <li key={e.id} className="flex items-center gap-2 py-1.5 px-2.5 rounded-lg bg-white dark:bg-gray-800 border border-gray-100 dark:border-gray-700">
              <div className="flex-1 min-w-0 text-xs">
                <span className="font-medium text-gray-900 dark:text-gray-100">{e.email}</span>
                {e.label && <span className="ml-2 text-gray-400 dark:text-gray-500">({e.label})</span>}
                {e.is_primary && e.status === 'approved' && (
                  <span className="ml-2 font-medium text-indigo-600 dark:text-indigo-400">{t('common.primary')}</span>
                )}
                {e.status === 'pending_user' && (
                  <span className="ml-2 inline-flex items-center gap-1 font-medium text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/20 px-1.5 py-0.5 rounded-full">
                    {t('account_settings.email_manager.status_pending_user')}
                  </span>
                )}
                {e.status === 'pending_admin' && (
                  <span className="ml-2 inline-flex items-center gap-1 font-medium text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/20 px-1.5 py-0.5 rounded-full">
                    {t('account_settings.email_manager.status_pending_admin')}
                  </span>
                )}
                {e.status === 'rejected' && (
                  <span className="ml-2 inline-flex items-center gap-1 font-medium text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 px-1.5 py-0.5 rounded-full">
                    {t('account_settings.email_manager.status_rejected')}
                  </span>
                )}
              </div>
              <div className="flex items-center gap-2 shrink-0">
                {!e.is_primary && e.status === 'approved' && (
                  <button onClick={() => handleSetPrimary(e.id)} className="text-xs text-indigo-600 dark:text-indigo-400 hover:underline">
                    {t('common.make_primary')}
                  </button>
                )}
                {emails.length > 1 && (
                  <button onClick={() => handleDelete(e.id)} className="text-xs text-red-500 hover:text-red-700 hover:underline">
                    {t('common.delete')}
                  </button>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}

      <form onSubmit={handleAdd} className="flex gap-2 flex-wrap">
        <input
          type="email"
          value={newEmail}
          onChange={e => setNewEmail(e.target.value)}
          placeholder={t('account_settings.email_manager.placeholder_email')}
          required
          className="flex-1 min-w-0 border border-gray-300 dark:border-gray-600 rounded-lg px-2 py-1 text-xs focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:bg-gray-800 dark:text-gray-100"
        />
        <input
          type="text"
          value={newLabel}
          onChange={e => setNewLabel(e.target.value)}
          placeholder={t('account_settings.email_manager.placeholder_label')}
          maxLength={100}
          className="w-32 border border-gray-300 dark:border-gray-600 rounded-lg px-2 py-1 text-xs focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:bg-gray-800 dark:text-gray-100"
        />
        <button
          type="submit"
          disabled={adding || !newEmail.trim()}
          className="bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-white text-xs font-medium px-3 py-1 rounded-lg transition-colors shrink-0"
        >
          {adding ? t('account_settings.email_manager.adding') : t('common.add')}
        </button>
      </form>
    </div>
  )
}

export function UserManagement() {
  const { t } = useTranslation()
  const { setError, currentUser } = useStore()
  const [users, setUsers] = useState<User[]>([])
  const [loading, setLoading] = useState(true)

  const [editingId, setEditingId] = useState<number | null>(null)
  const [editState, setEditState] = useState<EditState>(EMPTY_EDIT)
  const [manageEmailsId, setManageEmailsId] = useState<number | null>(null)

  const [creating, setCreating] = useState(false)
  const [newUser, setNewUser] = useState({ username: '', password: '', email: '', initials: '', is_admin: false })
  const [saving, setSaving] = useState(false)
  const [successMsg, setSuccessMsg] = useState<string | null>(null)

  const load = async () => {
    setLoading(true)
    try {
      setUsers(await getUsers())
    } catch (e) {
      setError(e instanceof Error ? e.message : t('user_management.error_load_users'))
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { load() }, [])

  const flash = (msg: string) => {
    setSuccessMsg(msg)
    setTimeout(() => setSuccessMsg(null), 3000)
  }

  const startEdit = (u: User) => {
    setEditingId(u.id)
    setEditState({ email: u.email ?? '', initials: u.initials ?? '', is_admin: u.is_admin, password: '' })
  }

  const cancelEdit = () => { setEditingId(null); setEditState(EMPTY_EDIT) }

  const saveEdit = async (id: number) => {
    setSaving(true)
    try {
      const payload: Parameters<typeof updateUser>[1] = {
        email: editState.email || undefined,
        initials: editState.initials || undefined,
        is_admin: editState.is_admin,
      }
      if (editState.password) payload.password = editState.password
      await updateUser(id, payload)
      cancelEdit()
      await load()
      flash(t('user_management.flash_user_updated'))
    } catch (e) {
      setError(e instanceof Error ? e.message : t('user_management.error_save'))
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async (u: User) => {
    if (!window.confirm(t('user_management.confirm_delete', { username: u.username }))) return
    try {
      await deleteUser(u.id)
      await load()
      flash(t('user_management.flash_user_deleted'))
    } catch (e) {
      setError(e instanceof Error ? e.message : t('user_management.error_delete'))
    }
  }

  const handleRequireMfa = async (u: User) => {
    try {
      await updateUser(u.id, { mfa_required: true })
      await load()
      flash(t('user_management.flash_mfa_required', { username: u.username }))
    } catch (e) {
      setError(e instanceof Error ? e.message : t('user_management.error_mfa_set'))
    }
  }

  const handleUnrequireMfa = async (u: User) => {
    try {
      await updateUser(u.id, { mfa_required: false })
      await load()
      flash(t('user_management.flash_mfa_unrequired', { username: u.username }))
    } catch (e) {
      setError(e instanceof Error ? e.message : t('user_management.error_mfa_unset'))
    }
  }

  const handleDeactivate = async (u: User) => {
    if (!window.confirm(t('user_management.confirm_deactivate', { username: u.username }))) return
    try {
      await updateUser(u.id, { is_active: false })
      await load()
      flash(t('user_management.flash_user_deactivated', { username: u.username }))
    } catch (e) {
      setError(e instanceof Error ? e.message : t('user_management.error_activation_set'))
    }
  }

  const handleActivate = async (u: User) => {
    try {
      await updateUser(u.id, { is_active: true })
      await load()
      flash(t('user_management.flash_user_activated', { username: u.username }))
    } catch (e) {
      setError(e instanceof Error ? e.message : t('user_management.error_activation_set'))
    }
  }

  const handleResetMfa = async (u: User) => {
    if (!window.confirm(t('user_management.confirm_reset_mfa', { username: u.username }))) return
    try {
      await resetUserMfa(u.id)
      await load()
      flash(t('user_management.flash_mfa_reset'))
    } catch (e) {
      setError(e instanceof Error ? e.message : t('user_management.error_mfa_reset'))
    }
  }

  const handleResendWelcome = async (u: User) => {
    if (!window.confirm(t('user_management.confirm_resend_welcome', { email: u.email, username: u.username }))) return
    try {
      const result = await resendWelcomeEmail(u.id)
      flash(result.message)
    } catch (e) {
      setError(e instanceof Error ? e.message : t('user_management.error_resend_welcome'))
    }
  }

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!newUser.username || !newUser.password) return
    setSaving(true)
    try {
      await createUser({
        username: newUser.username,
        password: newUser.password,
        email: newUser.email || undefined,
        initials: newUser.initials || undefined,
        is_admin: newUser.is_admin,
      })
      setNewUser({ username: '', password: '', email: '', initials: '', is_admin: false })
      setCreating(false)
      await load()
      flash(t('user_management.flash_user_created'))
    } catch (e) {
      setError(e instanceof Error ? e.message : t('user_management.error_create'))
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return <p className="text-sm text-gray-500">{t('user_management.loading')}</p>
  }

  return (
    <div className="space-y-6">
      {successMsg && (
        <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 text-green-700 dark:text-green-300 rounded-xl px-4 py-2 text-sm">
          {successMsg}
        </div>
      )}

      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300">{t('user_management.heading_users', { count: users.length })}</h3>
        {!creating && (
          <button
            onClick={() => setCreating(true)}
            className="text-xs bg-blue-600 hover:bg-blue-700 text-white px-3 py-1.5 rounded-lg font-medium transition-colors"
          >
            {t('user_management.button_new_user')}
          </button>
        )}
      </div>

      {creating && (
        <form onSubmit={handleCreate} className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800/50 rounded-xl p-4 space-y-3">
          <p className="text-sm font-semibold text-blue-800 dark:text-blue-300">{t('user_management.form_title_new_user')}</p>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">{t('user_management.label_username_required')}</label>
              <input
                type="text"
                value={newUser.username}
                onChange={(e) => setNewUser({ ...newUser, username: e.target.value })}
                className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-2 py-1.5 text-sm bg-white dark:bg-gray-800 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">{t('user_management.label_password_required')}</label>
              <input
                type="password"
                value={newUser.password}
                onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-2 py-1.5 text-sm bg-white dark:bg-gray-800 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
                minLength={8}
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">{t('user_management.label_email')}</label>
              <input
                type="email"
                value={newUser.email}
                onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-2 py-1.5 text-sm bg-white dark:bg-gray-800 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">{t('user_management.label_initials')}</label>
              <input
                type="text"
                value={newUser.initials}
                onChange={(e) => setNewUser({ ...newUser, initials: e.target.value })}
                className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-2 py-1.5 text-sm bg-white dark:bg-gray-800 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>
          <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
            <input
              type="checkbox"
              checked={newUser.is_admin}
              onChange={(e) => setNewUser({ ...newUser, is_admin: e.target.checked })}
              className="rounded"
            />
            {t('user_management.label_admin')}
          </label>
          <div className="flex gap-2">
            <button
              type="submit"
              disabled={saving}
              className="text-xs bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white px-3 py-1.5 rounded-lg font-medium transition-colors"
            >
              {t('user_management.button_save')}
            </button>
            <button
              type="button"
              onClick={() => setCreating(false)}
              className="text-xs text-gray-500 hover:text-gray-700 px-3 py-1.5"
            >
              {t('user_management.button_cancel')}
            </button>
          </div>
        </form>
      )}

      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
        <div className="overflow-x-auto">
        <table className="w-full text-sm min-w-[900px]">
          <thead>
            <tr className="bg-gray-50 dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700">
              <th className="px-4 py-3 text-left font-medium text-gray-600 dark:text-gray-400">{t('user_management.col_username')}</th>
              <th className="px-4 py-3 text-left font-medium text-gray-600 dark:text-gray-400">{t('user_management.col_email')}</th>
              <th className="px-4 py-3 text-left font-medium text-gray-600 dark:text-gray-400">{t('user_management.col_initials')}</th>
              <th className="px-4 py-3 text-center font-medium text-gray-600 dark:text-gray-400">{t('user_management.col_role')}</th>
              <th className="px-4 py-3 text-center font-medium text-gray-600 dark:text-gray-400">{t('user_management.col_status')}</th>
              <th className="px-4 py-3 text-center font-medium text-gray-600 dark:text-gray-400">{t('user_management.col_mfa')}</th>
              <th className="px-4 py-3" />
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
            {users.map((u) => (
              <Fragment key={u.id}>
              {editingId === u.id ? (
                <tr className="bg-blue-50 dark:bg-blue-900/20">
                  <td className="px-4 py-3 font-medium text-gray-900 dark:text-gray-100">{u.username}</td>
                  <td className="px-4 py-3">
                    <input
                      type="email"
                      value={editState.email}
                      onChange={(e) => setEditState({ ...editState, email: e.target.value })}
                      className="w-full border border-gray-300 dark:border-gray-600 rounded px-2 py-1 text-xs bg-white dark:bg-gray-800 dark:text-gray-100"
                      placeholder={t('user_management.placeholder_email')}
                    />
                  </td>
                  <td className="px-4 py-3">
                    <input
                      type="text"
                      value={editState.initials}
                      onChange={(e) => setEditState({ ...editState, initials: e.target.value })}
                      className="w-24 border border-gray-300 dark:border-gray-600 rounded px-2 py-1 text-xs bg-white dark:bg-gray-800 dark:text-gray-100"
                      placeholder={t('user_management.placeholder_initials')}
                    />
                  </td>
                  <td className="px-4 py-3 text-center">
                    <label className="flex items-center justify-center gap-1 text-xs">
                      <input
                        type="checkbox"
                        checked={editState.is_admin}
                        onChange={(e) => setEditState({ ...editState, is_admin: e.target.checked })}
                      />
                      {t('user_management.label_admin_short')}
                    </label>
                  </td>
                  <td className="px-4 py-3 text-center text-xs text-gray-400 dark:text-gray-500">—</td>
                  <td className="px-4 py-3 text-center text-xs text-gray-400 dark:text-gray-500">—</td>
                  <td className="px-4 py-3">
                    <div className="space-y-1.5">
                      <input
                        type="password"
                        value={editState.password}
                        onChange={(e) => setEditState({ ...editState, password: e.target.value })}
                        className="w-full border border-gray-300 dark:border-gray-600 rounded px-2 py-1 text-xs bg-white dark:bg-gray-800 dark:text-gray-100"
                        placeholder={t('user_management.placeholder_new_password')}
                        minLength={8}
                      />
                      <div className="flex gap-1">
                        <button
                          onClick={() => saveEdit(u.id)}
                          disabled={saving}
                          className="text-xs bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white px-2 py-1 rounded font-medium"
                        >
                          {t('user_management.button_save')}
                        </button>
                        <button
                          onClick={cancelEdit}
                          className="text-xs text-gray-500 hover:text-gray-700 px-2 py-1"
                        >
                          {t('user_management.button_cancel')}
                        </button>
                      </div>
                    </div>
                  </td>
                </tr>
              ) : (
                <tr className="hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
                  <td className="px-4 py-3 font-medium text-gray-900 dark:text-gray-100">{u.username}</td>
                  <td className="px-4 py-3 text-gray-600 dark:text-gray-400">{u.email ?? <span className="text-gray-300 dark:text-gray-600">—</span>}</td>
                  <td className="px-4 py-3 text-gray-600 dark:text-gray-400 font-mono text-xs">{u.initials ?? <span className="text-gray-300 dark:text-gray-600">—</span>}</td>
                  <td className="px-4 py-3 text-center">
                    {u.is_admin ? (
                      <span className="bg-purple-100 text-purple-700 text-xs font-medium px-2 py-0.5 rounded-full">{t('user_management.role_admin')}</span>
                    ) : (
                      <span className="text-xs text-gray-400 dark:text-gray-500">{t('user_management.role_user')}</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-center">
                    {u.is_active ? (
                      <span className="text-green-600 text-xs font-medium">{t('user_management.status_active')}</span>
                    ) : (
                      <span className="bg-gray-200 dark:bg-gray-700 text-gray-500 dark:text-gray-400 text-xs font-medium px-2 py-0.5 rounded-full">
                        {t('user_management.status_disabled')}
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-center">
                    {u.mfa_enabled ? (
                      <span className="text-green-600 text-xs font-medium">{t('user_management.mfa_on')}</span>
                    ) : (
                      <span className="text-gray-300 dark:text-gray-600 text-xs">{t('user_management.mfa_off')}</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right space-x-2 whitespace-nowrap">
                    <button
                      onClick={() => startEdit(u)}
                      className="text-blue-600 hover:underline text-xs font-medium"
                    >
                      {t('user_management.button_edit')}
                    </button>
                    {!u.mfa_enabled && !u.mfa_required && (
                      <button
                        onClick={() => handleRequireMfa(u)}
                        className="text-indigo-600 hover:underline text-xs"
                      >
                        {t('user_management.button_require_mfa')}
                      </button>
                    )}
                  {!u.mfa_enabled && u.mfa_required && (
                      <>
                        <span className="text-xs text-amber-600 font-medium">{t('user_management.badge_mfa_required')}</span>
                        <button
                          onClick={() => handleUnrequireMfa(u)}
                          className="text-amber-600 hover:underline text-xs"
                          title={t('user_management.tooltip_unrequire_mfa')}
                        >
                          {t('user_management.button_unrequire_mfa')}
                        </button>
                      </>
                    )}
                  {u.mfa_enabled && (
                      <button
                        onClick={() => handleResetMfa(u)}
                        className="text-orange-500 hover:underline text-xs"
                      >
                        {t('user_management.button_reset_mfa')}
                      </button>
                    )}
                    {u.email && (
                      <button
                        onClick={() => handleResendWelcome(u)}
                        className="text-teal-600 hover:underline text-xs"
                      >
                        {t('user_management.button_resend_welcome')}
                      </button>
                    )}
                    {u.id !== currentUser?.id && (
                      u.is_active ? (
                        <button
                          onClick={() => handleDeactivate(u)}
                          className="text-gray-500 hover:underline text-xs"
                        >
                          {t('user_management.button_deactivate_user')}
                        </button>
                      ) : (
                        <button
                          onClick={() => handleActivate(u)}
                          className="text-green-600 hover:underline text-xs"
                        >
                          {t('user_management.button_activate_user')}
                        </button>
                      )
                    )}
                    <button
                      onClick={() => setManageEmailsId(manageEmailsId === u.id ? null : u.id)}
                      className="text-indigo-500 hover:underline text-xs"
                    >
                      {manageEmailsId === u.id
                        ? t('user_management.button_hide_emails')
                        : t('user_management.button_manage_emails')}
                    </button>
                    <button
                      onClick={() => handleDelete(u)}
                      className="text-red-400 hover:underline text-xs"
                    >
                      {t('user_management.button_delete')}
                    </button>
                  </td>
                </tr>
              )}
              {manageEmailsId === u.id && (
                <tr className="bg-gray-50 dark:bg-gray-900">
                  <td colSpan={7} className="px-4 py-3">
                    <p className="text-xs font-semibold text-gray-600 dark:text-gray-400 mb-2">
                      {t('user_management.email_manager.heading', { username: u.username })}
                    </p>
                    <UserEmailManager userId={u.id} />
                  </td>
                </tr>
              )}
              </Fragment>
            ))}
          </tbody>
        </table>
        </div>
      </div>
    </div>
  )
}
